/**
 * ガチャの模擬抽選(docs/20 §5「模擬抽選(1,000回の結果表)」)。ブラウザ内で回すだけで、何も保存しない。
 *
 * 規則はサーバーの抽選(functions/src/gacha.ts)と同じ:
 *   1. 当月に出る枠だけでプールを作る(`months` の無い枠は恒常)
 *   2. プールに実在するレアリティだけを候補にし、ガチャの `rarityRates` を正規化してレアリティを決める
 *   3. そのレアリティの中で `weight` の重み付き抽選
 *   4. `guarantee.count` 連で `guarantee.rarity` 以上が1つも無ければ、最後の枠を保証枠で引き直す
 * packages/engine に共通の抽選ロジックが無いため、管理画面内の純関数として持つ。
 */
import { RARITIES, type GachaDef, type ItemDef, type Rarity } from '@paltyping/content-schema';

export type Rng = () => number;

/** 再現できる乱数(mulberry32)。テストと「同じ結果をもう一度」に使う。 */
export function seededRng(seed: number): Rng {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** サービスの現地時間(JST)での当月 `YYYY-MM`(サーバーの gachaMonth と同じ考え方)。 */
export function jstMonth(now: Date): string {
  return new Date(now.getTime() + 9 * 60 * 60_000).toISOString().slice(0, 7);
}

interface SimEntry {
  readonly itemId: string;
  readonly weight: number;
  readonly rarity: Rarity;
}

function rank(rarity: Rarity): number {
  return RARITIES.indexOf(rarity);
}

export function buildSimPool(
  gacha: GachaDef,
  itemsById: ReadonlyMap<string, ItemDef>,
  month: string,
): SimEntry[] {
  const pool: SimEntry[] = [];
  for (const entry of gacha.pool) {
    if (entry.months !== undefined && !entry.months.includes(month)) continue;
    const item = itemsById.get(entry.itemId);
    if (!item || !(entry.weight > 0)) continue;
    pool.push({ itemId: entry.itemId, weight: entry.weight, rarity: item.rarity });
  }
  return pool;
}

export class GachaSimError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'GachaSimError';
  }
}

function pickRarity(
  pool: readonly SimEntry[],
  rates: Readonly<Record<Rarity, number>>,
  rng: Rng,
  minRarity: Rarity,
): Rarity {
  const candidates = RARITIES.filter(
    (rarity) => rank(rarity) >= rank(minRarity) && pool.some((entry) => entry.rarity === rarity),
  );
  if (candidates.length === 0) {
    throw new GachaSimError(`${minRarity} 以上のアイテムがプールにありません`);
  }
  const total = candidates.reduce((sum, rarity) => sum + (rates[rarity] ?? 0), 0);
  let threshold = rng() * total;
  for (const rarity of candidates) {
    threshold -= rates[rarity] ?? 0;
    if (threshold < 0) return rarity;
  }
  return candidates[candidates.length - 1] as Rarity;
}

function pickFromRarity(pool: readonly SimEntry[], rarity: Rarity, rng: Rng): SimEntry {
  const entries = pool.filter((entry) => entry.rarity === rarity);
  const total = entries.reduce((sum, entry) => sum + entry.weight, 0);
  let threshold = rng() * total;
  for (const entry of entries) {
    threshold -= entry.weight;
    if (threshold < 0) return entry;
  }
  return entries[entries.length - 1] as SimEntry;
}

function drawOne(
  pool: readonly SimEntry[],
  rates: Readonly<Record<Rarity, number>>,
  rng: Rng,
  minRarity: Rarity = 'N',
): SimEntry {
  return pickFromRarity(pool, pickRarity(pool, rates, rng, minRarity), rng);
}

/** count 連(count が保証の連数以上なら保証を効かせる)。 */
export function drawBatch(
  pool: readonly SimEntry[],
  gacha: Pick<GachaDef, 'rarityRates' | 'guarantee'>,
  count: number,
  rng: Rng,
): SimEntry[] {
  const results: SimEntry[] = [];
  for (let i = 0; i < count; i += 1) results.push(drawOne(pool, gacha.rarityRates, rng));
  const guarantee = gacha.guarantee;
  if (count >= guarantee.count) {
    const min = rank(guarantee.rarity);
    if (!results.some((entry) => rank(entry.rarity) >= min)) {
      results[results.length - 1] = drawOne(pool, gacha.rarityRates, rng, guarantee.rarity);
    }
  }
  return results;
}

export type SimMode = 'single' | 'multi';

export interface SimResult {
  readonly draws: number;
  readonly byRarity: Readonly<Record<Rarity, number>>;
  /** 出た回数の多い順。 */
  readonly byItem: readonly {
    readonly itemId: string;
    readonly rarity: Rarity;
    readonly count: number;
  }[];
  /** 1回も出なかったプール内のアイテム数。 */
  readonly neverDrawn: number;
}

/**
 * `draws` 回の模擬抽選。`single` は単発を draws 回、`multi` は guarantee.count 連を
 * draws / count 回(端数は単発)。
 */
export function simulateGacha(
  gacha: GachaDef,
  itemsById: ReadonlyMap<string, ItemDef>,
  options: { readonly draws: number; readonly mode: SimMode; readonly month: string },
  rng: Rng,
): SimResult {
  const pool = buildSimPool(gacha, itemsById, options.month);
  if (pool.length === 0) throw new GachaSimError('当月に出るアイテムがプールにありません');
  const drawn: SimEntry[] = [];
  if (options.mode === 'multi' && gacha.guarantee.count > 1) {
    const size = gacha.guarantee.count;
    const batches = Math.floor(options.draws / size);
    for (let i = 0; i < batches; i += 1) drawn.push(...drawBatch(pool, gacha, size, rng));
    const rest = options.draws - batches * size;
    for (let i = 0; i < rest; i += 1) drawn.push(...drawBatch(pool, gacha, 1, rng));
  } else {
    for (let i = 0; i < options.draws; i += 1) drawn.push(...drawBatch(pool, gacha, 1, rng));
  }
  const byRarity = { N: 0, R: 0, SR: 0, UR: 0 } as Record<Rarity, number>;
  const counts = new Map<string, { itemId: string; rarity: Rarity; count: number }>();
  for (const entry of drawn) {
    byRarity[entry.rarity] += 1;
    const current = counts.get(entry.itemId);
    if (current) current.count += 1;
    else counts.set(entry.itemId, { itemId: entry.itemId, rarity: entry.rarity, count: 1 });
  }
  const byItem = [...counts.values()].sort(
    (a, b) => b.count - a.count || (a.itemId < b.itemId ? -1 : 1),
  );
  const poolIds = new Set(pool.map((entry) => entry.itemId));
  return { draws: drawn.length, byRarity, byItem, neverDrawn: poolIds.size - counts.size };
}
